// src/layouts/DashboardLayout.jsx
import React, { useContext, useState } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, Settings, UserCircle } from "lucide-react";
import { FiSend, FiUsers, FiGrid, FiFileText } from "react-icons/fi";
import {
	Avatar,
	Menu as MuiMenu,
	MenuItem,
	IconButton,
	Tooltip,
} from "@mui/material";
import logo from "../assets/aedc.png";
import backgroundImage from "../assets/redpay_logo.jpg";
import { MdInsertDriveFile } from "react-icons/md";
import { decryptData } from "../utils/crypto";
import { AuthContext } from "../context/AuthContext";
import BRANDING from "../config/branding";

const DashboardLayout = () => {
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);
	const location = useLocation();
	const { logout } = useContext(AuthContext);

	const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
	const handleMenuClose = () => setAnchorEl(null);

	const encryptedRole = localStorage.getItem("_x9Rk1");
	const role = encryptedRole ? decryptData(encryptedRole) : null;

	const handleLogout = async () => {
		localStorage.clear();
		sessionStorage.clear();
		await logout();
		navigate("/");
	};

	const formatRole = (role) => {
		if (!role) return "";
		switch (role) {
			case "admin":
				return "ADMIN";
			case "customer_support":
				return "Customer Support";
			case "technical_support":
				return "Technical Support";
			default:
				return role;
		}
	};

	const email = localStorage.getItem("email");

	const sidebarLinks = [
		{ label: "Dashboard", icon: <FiGrid />, path: "/dashboard" },
		{
			label: "Bulk SMS",
			icon: <FiSend />,
			path: "/dashboard/send-multiple-sms",
			//allowedRoles: ["customer_support"],
		},
		{
			label: "Uploaded Files",
			icon: <FiFileText />,
			path: "/dashboard/uploaded_files",
			//allowedRoles: ["customer_support"],
		},
		{
		label: "Send SMS",
		icon: <FiSend />,
		path: "/dashboard/send-sms",
	},
		// {
		// 	label: "Approve Files",
		// 	icon: <FiFileText />,
		// 	path: "/dashboard/approve-file",
		// 	allowedRoles: ["admin", "super_admin"],
		// },
		// {
		// 	label: "Approved Files",
		// 	icon: <FiFileText />,
		// 	path: "/dashboard/approved-files",
		// 	allowedRoles: ["admin", "super_admin"],
		// },
		{ label: "Search", icon: <FiSend />, path: "/dashboard/search" },
		{
			label: "Manage SMS Category",
			icon: <FiSend />,
			path: "/dashboard/categories",
		},
		{ label: "Sent Messages", icon: <FiSend />, path: "/dashboard/sent" },
		{ label: "Summary", icon: <FiSend />, path: "/dashboard/message-summary" },
		{
			label: "Users",
			icon: <FiUsers />,
			path: "/dashboard/users",
			allowedRoles: ["admin", "super_admin"],
		},
		{
			label: "Create User",
			icon: <FiUsers />,
			path: "/dashboard/create_user",
			allowedRoles: ["admin", "super_admin"],
		},
		{
			label: "Logs",
			icon: <MdInsertDriveFile />,
			path: "/dashboard/logs",
			allowedRoles: ["admin", "super_admin"],
		},
		{
			label: "Audit Logs",
			icon: <MdInsertDriveFile />,
			path: "/dashboard/audit-logs",
			allowedRoles: ["admin", "super_admin"],
		},
	];

	return (
		<div className='flex h-screen overflow-hidden bg-white'>
			{/* Sidebar */}
			<aside className='w-64 flex-shrink-0 bg-white border-r border-[#1E2F97]/30 p-6 shadow-md h-screen overflow-y-auto'>
				<div className='flex items-center p-4 rounded-lg shadow space-x-4 bg-white border border-[#1E2F97]/20'>
					<img
						src={BRANDING.logo}
						alt={BRANDING.shortName}
						className='w-10 h-10 rounded-full'
					/>
					<h2 className='text-xl font-bold text-red-600'>
						{BRANDING.shortName}
					</h2>
				</div>

				<nav className='space-y-2 mt-4'>
					{sidebarLinks
						.filter(
							(link) => !link.allowedRoles || link.allowedRoles.includes(role)
						)
						.map((link) => (
							<Link
								key={link.path}
								to={link.path}
								className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
									location.pathname === link.path
										? "bg-red-600  text-white font-semibold shadow-md"
										: "text-red-600 hover:bg-[#1E2F97]/10 hover:shadow-sm"
								}`}
							>
								<span className='text-lg'>{link.icon}</span>
								<span className='font-medium'>{link.label}</span>
							</Link>
						))}
				</nav>
			</aside>

			{/* Main Content Wrapper */}
			<div className='flex-1 flex flex-col h-screen overflow-hidden'>
				{/* Header */}
				<header
	className='flex justify-between items-center text-white p-4 shadow-sm flex-shrink-0'
	style={{ backgroundColor: BRANDING.primaryColor }}
>
					<div>
						 <h1 className='text-2xl font-bold'>
							{BRANDING.portalTitle}
						</h1>

						<p className='text-sm opacity-90'>
							{BRANDING.portalSubtitle}
						</p>
					</div>

					<div className='flex items-center gap-4'>
		<div className='flex flex-col items-end'>
    <span className='text-sm font-medium text-white'>
        {email}
    </span>

    <span className='text-xs opacity-80 uppercase'>
        {formatRole(role)}
    </span>
</div>
						<Tooltip title='Account Settings'>
							<IconButton onClick={handleMenuOpen}>
								<Avatar
	sx={{
		bgcolor: "#fff",
		color: BRANDING.primaryColor,
	}}
>
									<UserCircle size={20} />
								</Avatar>
							</IconButton>
						</Tooltip>
						<MuiMenu
							anchorEl={anchorEl}
							open={open}
							onClose={handleMenuClose}
							onClick={handleMenuClose}
							PaperProps={{
								elevation: 2,
								sx: { mt: 1, borderRadius: 2, minWidth: 180 },
							}}
						>
							<MenuItem onClick={() => navigate("/dashboard/settings")}>
								<Settings size={16} className='mr-2' color={BRANDING.primaryColor} /> Settings
							</MenuItem>
							<MenuItem onClick={handleLogout}>
								<LogOut size={16} className='mr-2' color={BRANDING.primaryColor} /> Logout
							</MenuItem>
						</MuiMenu>
					</div>
				</header>

				{/* Scrollable Content */}
				<main className='relative flex-1 overflow-y-auto p-6 pb-20 bg-white'>
					<div className='relative min-h-full'>
						{/* Background watermark */}
						<div
							className='absolute inset-0 z-0'
							style={{
								backgroundImage: `url(${BRANDING.logo})`,
								backgroundSize: "cover",
								backgroundPosition: "center",
								backgroundRepeat: "no-repeat",
								opacity: 0.05,
							}}
						></div>

						{/* Foreground */}
						<div className='relative z-10 bg-white p-4 rounded shadow'>
							<Outlet />
						</div>
					</div>
				</main>
			</div>
		</div>
	);
};

export default DashboardLayout;
